import { useEffect, useState } from "react";
import { Eye, Loader2, RotateCcw, Save, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg } from "@/lib/api";

const Editor = ({ t, onSaved }) => {
  const [f, setF] = useState(t);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { setF(t); setPreview(null); }, [t]);
  const body = { subject: f.subject, body: f.body, button: f.button, enabled: f.enabled };
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "save") { onSaved(await api.put(`/superadmin/emails/${t.key}`, body).then((r) => r.data)); toast.success("Email enregistré"); }
      if (kind === "reset") { onSaved(await api.delete(`/superadmin/emails/${t.key}`).then((r) => r.data)); toast.success("Texte d'origine rétabli"); }
      if (kind === "preview") setPreview((await api.post(`/superadmin/emails/${t.key}/preview`, body)).data);
      if (kind === "test") toast.success(`Email de test envoyé à ${(await api.post(`/superadmin/emails/${t.key}/test`, body)).data.sent_to}`);
    } catch (e) { toast.error(errMsg(e)); }
    setBusy("");
  };
  const Btn = ({ kind, icon: Icon, label, variant = "outline" }) => (
    <Button variant={variant} size="sm" disabled={!!busy} onClick={() => run(kind)} data-testid={`email-${kind}-${t.key}`}>
      {busy === kind ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Icon className="h-4 w-4 mr-1.5" />}{label}
    </Button>
  );
  return (
    <div className="space-y-4" data-testid={`email-editor-${t.key}`}>
      <label className="flex items-center gap-2 text-sm"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid={`email-enabled-${t.key}`} />Envoyer cet email</label>
      <label className="block"><span className="text-xs text-slate-500">Objet</span><Input className="mt-1" value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid={`email-subject-${t.key}`} /></label>
      <label className="block"><span className="text-xs text-slate-500">Texte (une ligne vide = nouveau paragraphe)</span><Textarea className="mt-1 min-h-[220px]" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid={`email-body-${t.key}`} /></label>
      <label className="block"><span className="text-xs text-slate-500">Texte du bouton (vide = pas de bouton)</span><Input className="mt-1" value={f.button} onChange={(e) => setF({ ...f, button: e.target.value })} data-testid={`email-button-${t.key}`} /></label>
      <p className="text-xs text-slate-500">Variables disponibles : {t.vars.map((v) => <code key={v} className="mx-0.5 px-1 rounded bg-slate-100">{`{${v}}`}</code>)}</p>
      <div className="flex flex-wrap gap-2">
        <Btn kind="save" icon={Save} label="Enregistrer" variant="default" />
        <Btn kind="preview" icon={Eye} label="Aperçu" />
        <Btn kind="test" icon={Send} label="M'envoyer un test" />
        {t.customized && <Btn kind="reset" icon={RotateCcw} label="Texte d'origine" />}
      </div>
      {preview && (
        <div className="rounded-lg border border-slate-200 bg-white" data-testid={`email-preview-${t.key}`}>
          <p className="px-4 py-2 border-b border-slate-100 text-sm"><span className="text-slate-500">Objet :</span> {preview.subject}</p>
          <div className="px-4 py-3 text-sm space-y-3 [&_a]:inline-block" dangerouslySetInnerHTML={{ __html: preview.html }} />
        </div>
      )}
    </div>
  );
};

export const EmailsPanel = () => {
  const [list, setList] = useState(null);
  const [key, setKey] = useState("welcome");
  useEffect(() => { api.get("/superadmin/emails").then(({ data }) => setList(data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!list) return <Loader2 className="h-5 w-5 animate-spin text-slate-400" />;
  const t = list.find((x) => x.key === key);
  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-6 max-w-5xl" data-testid="sa-emails">
      <div className="space-y-1">
        {list.map((x) => (
          <button key={x.key} onClick={() => setKey(x.key)} data-testid={`email-tab-${x.key}`}
            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${x.key === key ? "bg-slate-200/80 font-semibold" : "hover:bg-slate-100"}`}>
            {x.label}{!x.enabled && <span className="ml-1 text-xs text-slate-400">(désactivé)</span>}{x.customized && <span className="ml-1 text-xs text-blue-600">· perso</span>}
          </button>
        ))}
        <p className="text-xs text-slate-500 px-3 pt-3">Les données de l'aperçu et du test sont des exemples.</p>
      </div>
      <Editor t={t} onSaved={(n) => setList((l) => l.map((x) => (x.key === n.key ? n : x)))} />
    </div>
  );
};
